import { useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { getEnvironmentType, PRODUCTION, STAGING, BETA } from '@cyberint/common-utils';

const PARAMS_CUSTOMER_ID = 'params_customer_id';
const SELECTED_CUSTOMER_ID = 'selected_customer_id';
const NA = 'N/A';

// For help, open ticket on DOPS team https://jira-prd.checkpoint.com/secure/CreateIssue!default.jspa
const DSNS = {
  DEVELOPMENT_DSN: 'https://873a8906341628a5312afb156ab93970@sentry-prod-relay.iaas.checkpoint.com/332',
  STAGING_DSN: 'https://4ca9bb773fa49fa6f767d1873b44f491@sentry-prod-relay.iaas.checkpoint.com/332',
  PRODUCTION_DSN: 'https://a1eb979e582b06202f0343644a450138@sentry-prod-relay.iaas.checkpoint.com/332',
  BETA_DSN: 'https://38b8b9b7ff8519f4f0273e4d02b47996@sentry-prod-relay.iaas.checkpoint.com/332'
};

const BREADCRUMB_CATEGORY = {
  CONSOLE: 'console',
  UI_CLICK: 'ui.click',
  XHR: 'xhr',
  FETCH: 'fetch'
};

const WHITELIST_XHR = [/\/alert\/alerts$/g, /\/mdf\/assets$/g, /\/mdf\/issues\/group$/g];

// This is left pretty empty so we will be able to filter out transactions if necessary
const beforeSendTransaction = event => event;

const NOT_AUTHORIZED = 401;

/**
 * Expired-session 401s are already handled (unauthorized-interceptor -> force-logout ->
 * expired-session dialog), but the interceptor re-throws so call sites keep their own
 * error handling - fire-and-forget calls then surface the same 401 as an unhandled
 * rejection. Those events carry no signal and burn the event quota, so drop them.
 * Explicitly captured 401s (Sentry.captureException) are kept.
 */
const isUnhandled401Rejection = (event, hint) => {
  const error = hint?.originalException;
  if (!error?.isAxiosError || error?.response?.status !== NOT_AUTHORIZED) return false;
  return Boolean(event?.exception?.values?.some(value => value?.mechanism?.type === 'onunhandledrejection'));
};

const beforeSend = (event, hint) => (isUnhandled401Rejection(event, hint) ? null : event);

// We would like to filter out requests and responses data because we are limited
// in the number of events we can send. So we send only the ones we want to track. More info can be found here:
// https://docs.sentry.io/platforms/javascript/enriching-events/breadcrumbs/#customize-breadcrumbs
const beforeBreadcrumb = breadcrumb => {
  if (breadcrumb?.category === BREADCRUMB_CATEGORY.CONSOLE || breadcrumb?.category === BREADCRUMB_CATEGORY.UI_CLICK) {
    return null;
  }
  if (breadcrumb?.category === BREADCRUMB_CATEGORY.XHR || breadcrumb?.category === BREADCRUMB_CATEGORY.FETCH) {
    if (WHITELIST_XHR.every(item => !item.test(breadcrumb.data?.url || ''))) {
      return null;
    }
  }
  return breadcrumb;
};

const tracePropagationTargets = [
  /** based on this reg-exp:
   * direct requests to s3 will not include sentry headers
   * only requests to cyberint.io domain should include sentry headers
   * except those ones that return redirection 302
   * Redirection (to s3 bucket or any other request) keeps sentry headers which are not honored by s3 cors policy.
   * */
  /^https:\/\/[a-zA-Z0-9-]+(\.staging)?\.cyberint\.io\/(?!report-collaboration\/download|report-collaboration\/thumbnail)/
];

export const initSentry = () => {
  const environment = getEnvironmentType();
  const commonSettings = {
    integrations: [Sentry.browserTracingIntegration()],
    environment,
    release: process.env.BUILD_TIMESTAMP,
    attachStacktrace: true,
    beforeSendTransaction,
    beforeSend,
    beforeBreadcrumb,
    ignoreErrors: [
      'Non-Error exception captured',
      'Non-Error promise rejection captured',
      /Loading (CSS )?chunk \d+ failed/,
      /ChunkLoadError/
    ]
  };

  // Sentry init() initializes Sentry SDK. If an SDK is not initialized,
  // the SDK will not send any data over the network, such as captured exceptions.
  switch (environment) {
    case PRODUCTION:
      Sentry.init({
        ...commonSettings,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets,
        integrations: [
          Sentry.browserTracingIntegration({ enableInp: true, interactionsSampleRate: 0.1 }),
          Sentry.replayIntegration({
            maskAllText: true,
            maskInputOptions: { password: true },
            blockAllMedia: true
          })
        ],
        dsn: DSNS.PRODUCTION_DSN,
        tracesSampleRate: 0.1
      });
      break;
    case BETA:
      Sentry.init({
        ...commonSettings,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets,
        integrations: [
          Sentry.browserTracingIntegration({ enableInp: true, interactionsSampleRate: 0.1 }),
          Sentry.replayIntegration({
            maskAllText: true,
            maskInputOptions: { password: true },
            blockAllMedia: true
          }),
          Sentry.httpContextIntegration(),
          Sentry.functionToStringIntegration()
        ],
        dsn: DSNS.BETA_DSN,
        tracesSampleRate: 0.1
      });
      break;
    case STAGING:
      Sentry.init({
        ...commonSettings,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets,
        integrations: [
          Sentry.browserTracingIntegration({ enableInp: true, interactionsSampleRate: 0.2 }),
          Sentry.replayIntegration({
            maskAllText: true,
            maskInputOptions: { password: true },
            blockAllMedia: true
          }),
          Sentry.httpContextIntegration(),
          Sentry.functionToStringIntegration()
        ],
        dsn: DSNS.STAGING_DSN,
        tracesSampleRate: 0.2
      });
      break;
    default:
      Sentry.init({
        ...commonSettings,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
        tracePropagationTargets,
        integrations: [
          Sentry.browserTracingIntegration({ enableInp: true, interactionsSampleRate: 0.2 }),
          Sentry.replayIntegration({
            maskAllText: true,
            maskInputOptions: { password: true },
            blockAllMedia: true
          }),
          Sentry.httpContextIntegration(),
          Sentry.functionToStringIntegration()
        ],
        dsn: DSNS.DEVELOPMENT_DSN,
        tracesSampleRate: 0.2
      });
  }
};

export const SentryListenerBase = ({ customerId, currentUser, activeTabType, paramsCustomerId }) => {
  useEffect(() => {
    if (customerId && currentUser) {
      Sentry.setUser({ ...currentUser, id: currentUser._id });
      Sentry.setTag(SELECTED_CUSTOMER_ID, customerId);
    }
  }, [customerId, currentUser]);

  useEffect(() => {
    Sentry.setTag(PARAMS_CUSTOMER_ID, paramsCustomerId || NA);
  }, [paramsCustomerId]);

  useEffect(() => {
    if (currentUser?._id) {
      Sentry.addBreadcrumb({ category: 'auth', message: 'User has successfully logged in', level: 'info' });
    }
  }, [currentUser?._id]);

  useEffect(() => {
    if (activeTabType) {
      Sentry.addBreadcrumb({ category: 'navigation', message: `User navigated to ${activeTabType}`, level: 'info' });
    }
  }, [activeTabType]);

  return null;
};

SentryListenerBase.propTypes = {
  customerId: PropTypes.string,
  currentUser: PropTypes.object,
  activeTabType: PropTypes.string,
  paramsCustomerId: PropTypes.string
};
